import React, { useMemo } from 'react';
import { Avatar } from 'antd';
import { Product, ProductCategory } from 'api/types/product';
import ProductCartLabel from 'components/ProductCard/ProductCartLabel';
import {
  isExternalLink,
  isLocalProduct,
  isPreorderProduct,
  isRequest as isRequestProduct,
} from 'utils/product';
import { Button, CardHeader, Link, Modal, Typography } from 'ui-kit';
import { getCategoryForProductCard } from 'components/ProductCard/utils';
import placeholder from 'components/Dashboard/Store/Products/images/placeholder.png';
import './PreviewProductModal.scss';
import { calculateYouMake, formatCurrency } from 'utils';
import { useOwnerDetails } from 'api/owners';
import { PaymentPlan } from 'api/types/owner';
import { UrlText } from 'components/UrlText';
import OwnerProfitTooltip from 'ui-kit/OwnerProfitTooltip';
import { EPriceType } from 'components/NewProductStepper/ProductBaseForm/constants';
import { useSignedUser } from 'api/auth';
import { useParams } from 'react-router-dom';
import { useStoreDetails } from 'api/store';

interface Props {
  product: Product;
  isOpen: boolean;
  onClose: () => void;
  onEdit: (product: Partial<Product>) => void;
}

const BASE_CLASS = 'PreviewProductModal';

const PreviewProductModal: React.FC<Props> = ({ product, isOpen, onClose, onEdit }) => {
  const {
    name,
    description,
    cancellationPolicy: cancellation_policy,
    category,
    subcategory,
    image,
    instructions,
    price,
    ownerLocalProductAmount,
    ownerLocalProductCommission,
    affiliateLink,
    externalLink,
    priceType,
    store,
    storeId,
    isPurchasableOffPlatform,
    instructionsOffPlatform,
  } = product ?? {};

  const hasCancellationPolicy =
    typeof cancellation_policy === 'string' && cancellation_policy.trim().length > 0;

  const { data: user } = useSignedUser();
  const { data } = useOwnerDetails(user?.owner_id);
  const params = useParams<{ storeId: string }>();
  const storeIdFromParams = useMemo(
    () => (params.storeId ? Number(params.storeId) : null),
    [params],
  );
  const { data: storeDetails } = useStoreDetails(storeIdFromParams);
  const { currency, commissionCutoff, commissionLow, commissionHigh } = storeDetails ?? {};

  const isPreorder = isPreorderProduct(product);
  const isRequest = isRequestProduct(product);
  const isLocal = isLocalProduct(product?.category);
  const isLink = isExternalLink(product);
  const hasPremiumPlan = data?.plan !== PaymentPlan.Basic;
  const youMake = calculateYouMake({
    isLocal,
    ownerLocalProductAmount,
    price,
    commissionLow,
    commissionHigh,
    commissionCutoff,
    hasPremiumPlan,
  });
  const getStoreAffiliateLink = (baseUrl: string | undefined) => {
    if (!baseUrl) return '';
    try {
      const url = new URL(baseUrl);
      url.searchParams.append('store', store ? store.id.toString() : storeId.toString());
      return url.href;
    } catch (e) {
      if (e instanceof TypeError) {
        // eslint-disable-next-line no-console
        console.error('Invalid URL', baseUrl);
        return '';
      }
      throw e;
    }
  };

  const externalUrl =
    category === ProductCategory.LINK ? externalLink : getStoreAffiliateLink(affiliateLink);
  return (
    <Modal className={BASE_CLASS} visible={isOpen} onCancel={onClose} footer={null}>
      <div className={`${BASE_CLASS}-container`}>
        <div className={`${BASE_CLASS}-container-media`}>
          {isPreorder && !isRequest && <ProductCartLabel label="Pre-order" />}
          {isRequest && <ProductCartLabel label="Request" />}
          <Avatar className={`${BASE_CLASS}-container-media__image`} src={image || placeholder} />
        </div>
        <div className={`${BASE_CLASS}-container-content`}>
          <CardHeader
            title={name}
            subheader={getCategoryForProductCard(category, subcategory, description)}
          />
          {priceType === EPriceType.VARIABLE ? (
            <Typography variant="h6" className={`${BASE_CLASS}-container-content__prices-title`}>
              Variable price
            </Typography>
          ) : (
            <div className={`${BASE_CLASS}-container-content__prices`}>
              {!isLink && (
                <Typography
                  variant="h6"
                  className={`${BASE_CLASS}-container-content__prices-title`}
                >
                  {priceType === EPriceType.MULTIPLE ? 'from' : ''}{' '}
                  {formatCurrency(price, currency)}
                </Typography>
              )}
              {(isLocal || !hasPremiumPlan) && !isLink && (
                <>
                  <Typography variant="caption">
                    Your estimated profit{' '}
                    <Typography
                      variant="body1"
                      color="secondary"
                      className={`${BASE_CLASS}-container-content__prices-title`}
                    >
                      {priceType === EPriceType.MULTIPLE ? 'from' : ''}{' '}
                      {formatCurrency(youMake, currency)}
                    </Typography>
                  </Typography>{' '}
                  <OwnerProfitTooltip />
                </>
              )}
              {isLink && isLocal && (
                <>
                  <Typography variant="caption">
                    YOU MAKE{' '}
                    <Typography
                      variant="body1"
                      color="primary"
                      className={`${BASE_CLASS}-container-content__prices-title`}
                    >
                      {parseFloat(ownerLocalProductCommission) * 100}% of sales made using your link
                    </Typography>
                  </Typography>{' '}
                  <OwnerProfitTooltip />
                </>
              )}
            </div>
          )}
          <Typography variant="body2" className={`${BASE_CLASS}-container-content__description`}>
            <UrlText text={description} />
          </Typography>

          {hasCancellationPolicy && (
            <div className={`${BASE_CLASS}-container-content__cancellation`}>
              <Typography
                variant="subtitle2"
                className={`${BASE_CLASS}-container-content__cancellation-subtitle`}
              >
                Cancellation Policy:
              </Typography>
              <Typography
                variant="body2"
                className={`${BASE_CLASS}-container-content__cancellation-paragraph`}
              >
                <UrlText text={cancellation_policy} />
              </Typography>
            </div>
          )}
          {isLink && (
            <>
              <div className={`${BASE_CLASS}-container-content__affiliate-info`}>
                <Typography
                  variant="subtitle2"
                  className={`${BASE_CLASS}-container-content__affiliate-subtitle`}
                >
                  Your link:
                </Typography>
                <Typography
                  variant="body2"
                  className={`${BASE_CLASS}-container-content__affiliate-link`}
                >
                  <Link target="_blank" href={externalUrl}>
                    {externalUrl}
                  </Link>
                </Typography>
              </div>
              {isLocal && (
                <div className={`${BASE_CLASS}-container-content__affiliate-info`}>
                  <Typography
                    variant="subtitle2"
                    className={`${BASE_CLASS}-container-content__affiliate-subtitle`}
                  >
                    Disclaimer:
                  </Typography>
                  <Typography
                    variant="body2"
                    className={`${BASE_CLASS}-container-content__affiliate-info-disclaimer`}
                  >
                    Guest must complete purchase using your link in order for you to receive
                    commission. Purchases may take up to 6 weeks to process.
                  </Typography>
                </div>
              )}
            </>
          )}
          {!isLink && (
            <div className={`${BASE_CLASS}-container-content__instructions`}>
              <Typography
                variant="subtitle2"
                className={`${BASE_CLASS}-container-content__instructions-subtitle`}
              >
                Instructions for {isPurchasableOffPlatform && 'non-Airbnb'} guests after purchase:
              </Typography>
              <Typography
                variant="body2"
                className={`${BASE_CLASS}-container-content__instructions-paragraph`}
              >
                {instructions ? <UrlText text={instructions} /> : '-'}
              </Typography>
            </div>
          )}
          {isPurchasableOffPlatform && (
            <div className={`${BASE_CLASS}-container-content__instructions`}>
              <Typography
                variant="subtitle2"
                className={`${BASE_CLASS}-container-content__instructions-subtitle`}
              >
                Instructions for Airbnb guests after purchase:
              </Typography>
              <Typography
                variant="body2"
                className={`${BASE_CLASS}-container-content__instructions-paragraph`}
              >
                {instructionsOffPlatform ? (
                  <div>
                    {instructionsOffPlatform.split('\n').map((line, index) => (
                      <p key={index}>{line}</p>
                    ))}
                  </div>
                ) : (
                  '-'
                )}
              </Typography>
            </div>
          )}
          {!isLocal && (
            <Button variant="text" color="primary" onClick={() => onEdit({ id: product.id })}>
              Edit product
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default PreviewProductModal;
